import { memo, useCallback, useEffect, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import AdditionalInfoModal from '#client/components/AdditionalInfoModal';
import DepositMoneyModal from '#client/components/MoveMoney/DepositMoneyModal';
import InstantDepositModal from '#client/components/MoveMoney/InstantDepositModal';
import SendMoneyModal from '#client/components/MoveMoney/SendMoneyModal';
import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import { KEY_PARAM_MODAL, MODAL_NAMES } from '#client/constants/modals';
import { MOVE_MONEY_STEPS_WITH_WIRE_TYPE } from '#client/constants/transfer';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import getLastSegmentUrl from '#client/utils/getLastSegmentUrl';

const emptyModal = {
  isShow: false,
  forceRedirection: false,
  Component: null,
  props: {}
};

const NOT_FOUND_MODAL = null;
const INSTANT_DEPOSIT_PAGES = ['dashboard-notfunded'];

function SharedNavigateModals() {
  const { isInvalidStatus } = useApplicationStatusContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentModalName = searchParams.get(KEY_PARAM_MODAL);
  const entryPage = getLastSegmentUrl(document.location.pathname);
  const [isEnableShowInstantDepositFlag, isLoadingShowInstantDepositFlag] = useFeatureFlag(
    RELEASE_SHOW_INSTANT_DEPOSIT
  );
  const isShowInstantDepositMoveMoneyModal =
    !isLoadingShowInstantDepositFlag && isEnableShowInstantDepositFlag;

  const handleClose = useCallback(() => {
    if (currentModalName !== NOT_FOUND_MODAL) {
      setSearchParams({}, { replace: true });
    }
  }, [currentModalName, setSearchParams]);

  const modal = useMemo(() => {
    const forceRedirection = isInvalidStatus;

    switch (currentModalName) {
      case MODAL_NAMES.SEND_MONEY:
        return {
          isShow: true,
          forceRedirection,
          Component: SendMoneyModal,
          props: {
            handleClose,
            steps: MOVE_MONEY_STEPS_WITH_WIRE_TYPE
          }
        };
      case MODAL_NAMES.DEPOSIT_MONEY:
        return {
          isShow: true,
          forceRedirection,
          Component: DepositMoneyModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.INSTANT_DEPOSIT_MODAL:
        return {
          isShow: isShowInstantDepositMoveMoneyModal,
          forceRedirection:
            !INSTANT_DEPOSIT_PAGES.includes(entryPage) || !isShowInstantDepositMoveMoneyModal,
          Component: InstantDepositModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.ADDITIONAL_INFO_MODAL:
        return {
          isShow: true,
          forceRedirection,
          Component: AdditionalInfoModal,
          props: {
            handleClose
          }
        };
      default:
        return { ...emptyModal, forceRedirection: true };
    }
  }, [
    isInvalidStatus,
    currentModalName,
    handleClose,
    entryPage,
    isShowInstantDepositMoveMoneyModal
  ]);

  useEffect(() => {
    if (modal.forceRedirection) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.forceRedirection]);

  if (currentModalName === NOT_FOUND_MODAL) {
    return null;
  }

  return modal.isShow && <modal.Component {...modal.props} />;
}

export default memo(SharedNavigateModals);
